import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "../lib/supabase";
import { getInvitationByToken, useInvitation } from "../lib/invitations";

const schema = z.object({
  fullName: z.string().min(2, "Nom requis"),
  password: z.string().min(8, "8 caractères minimum"),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type FormData = z.infer<typeof schema>;

const AuthInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [mode, setMode] = useState<"register" | "login">("register");
  const [invitation, setInvitation] = useState<{
    id: string;
    dossier_id: string;
    email: string;
    dossier_nom: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signupEmailSent, setSignupEmailSent] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!token) {
      setError("Lien d'invitation invalide");
      setLoading(false);
      return;
    }
    getInvitationByToken(token).then((inv) => {
      setInvitation(inv ?? null);
      if (!inv) setError("Lien d'invitation invalide ou expiré");
      setLoading(false);
    });
  }, [token]);

  useEffect(() => {
    if (!token || !invitation || loading) return;
    let cancelled = false;
    void (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      if (!session?.user?.email || cancelled) return;
      if (session.user.email.toLowerCase() !== invitation.email.toLowerCase()) return;
      try {
        const ok = await useInvitation(token);
        if (ok && !cancelled) {
          navigate("/dashboard");
          window.location.reload();
        }
      } catch {
        /* invitation déjà utilisée ou erreur réseau : l'utilisateur peut continuer manuellement */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, invitation, loading, navigate]);

  const onSubmit = async (data: FormData) => {
    if (!invitation || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data: signData, error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password: data.password,
        options: {
          data: { full_name: data.fullName },
          emailRedirectTo: `${window.location.origin}/auth/invite?token=${encodeURIComponent(token)}`,
        },
      });
      if (signUpError) throw signUpError;

      if (signData.session?.user) {
        const ok = await useInvitation(token);
        if (!ok) throw new Error("Impossible d'utiliser l'invitation");
        navigate("/dashboard");
        window.location.reload();
        return;
      }

      setSignupEmailSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (data: FormData) => {
    if (!invitation || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password: data.password,
      });
      if (loginError) throw loginError;

      const ok = await useInvitation(token);
      if (!ok) throw new Error("Impossible d'utiliser l'invitation");

      navigate("/dashboard");
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la connexion");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendSignupEmail = async () => {
    if (!invitation) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: invitation.email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/invite?token=${encodeURIComponent(token!)}`,
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de renvoyer l'email");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0A0A0A",
          color: "#9A8F7E",
          fontFamily: "'Sora', sans-serif",
        }}
      >
        Chargement...
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#0A0A0A",
          color: "#F5F0E8",
          fontFamily: "'Sora', sans-serif",
          padding: 24,
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: 16 }}>⚠</div>
        <div style={{ fontSize: "1.1rem", marginBottom: 24, textAlign: "center" }}>{error}</div>
        <a
          href="/"
          style={{
            color: "#F97316",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          Retour à l'accueil
        </a>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0A0A0A",
        fontFamily: "'Sora', sans-serif",
        padding: 24,
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&display=swap');`}</style>
      <div
        style={{
          maxWidth: 420,
          width: "100%",
          background: "#111109",
          border: "1px solid #1E1C14",
          borderRadius: 16,
          padding: 32,
        }}
      >
        <div
          style={{
            background: "rgba(249,115,22,0.1)",
            border: "1px solid rgba(249,115,22,0.3)",
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            color: "#F97316",
            fontSize: "0.9rem",
          }}
        >
          Vous avez été invité à accéder au dossier{" "}
          <strong style={{ color: "#F97316" }}>{invitation?.dossier_nom}</strong>
        </div>

        {signupEmailSent ? (
          <div style={{ color: "#F5F0E8", fontSize: "0.95rem", lineHeight: 1.55, marginBottom: 20 }}>
            <p style={{ margin: "0 0 12px" }}>
              Un email de confirmation a été envoyé à <strong>{invitation?.email}</strong>. Ouvrez-le et cliquez sur le lien pour activer votre compte.
            </p>
            <p style={{ margin: "0 0 16px", color: "#9A8F7E", fontSize: "0.85rem" }}>
              Après confirmation, revenez sur cette page (même lien d&apos;invitation) : l&apos;accès au dossier sera finalisé automatiquement si vous êtes connecté.
            </p>
            <button
              type="button"
              onClick={handleResendSignupEmail}
              disabled={submitting}
              style={{
                width: "100%",
                padding: 12,
                background: "#1E1C14",
                border: "1px solid #2A2520",
                borderRadius: 10,
                color: "#F5F0E8",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: submitting ? "not-allowed" : "pointer",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              {submitting ? "Envoi…" : "Renvoyer l'email de confirmation"}
            </button>
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                background: "#0A0A08",
                borderRadius: 10,
                padding: 4,
                marginBottom: 24,
              }}
            >
              {(["register", "login"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  style={{
                    flex: 1,
                    padding: 10,
                    border: "none",
                    borderRadius: 8,
                    background: mode === m ? "#F97316" : "transparent",
                    color: mode === m ? "#0A0A0A" : "#9A8F7E",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  {m === "register" ? "Créer mon compte" : "J'ai déjà un compte"}
                </button>
              ))}
            </div>

            {error && (
              <div
                style={{
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 20,
                  color: "#EF4444",
                  fontSize: "0.85rem",
                }}
              >
                {error}
              </div>
            )}

            <form
          onSubmit={
            mode === "register"
              ? form.handleSubmit(onSubmit)
              : form.handleSubmit(handleLogin)
          }
        >
          <input
            type="hidden"
            value={invitation?.email || ""}
            readOnly
          />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: "0.78rem", color: "#9A8F7E", marginBottom: 6, fontWeight: 600 }}>
              Email
            </label>
            <div
              style={{
                padding: "12px 16px",
                background: "#0A0A08",
                border: "1px solid #2A2520",
                borderRadius: 10,
                color: "#9A8F7E",
                fontSize: "0.9rem",
              }}
            >
              {invitation?.email}
            </div>
          </div>

          {mode === "register" && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: "0.78rem", color: "#9A8F7E", marginBottom: 6, fontWeight: 600 }}>
                Votre nom
              </label>
              <input
                {...form.register("fullName")}
                placeholder="Votre nom"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  background: "#0A0A08",
                  border: "1px solid #2A2520",
                  borderRadius: 10,
                  color: "#F5F0E8",
                  fontSize: "0.9rem",
                  fontFamily: "'Sora', sans-serif",
                  outline: "none",
                }}
              />
              {form.formState.errors.fullName && (
                <div style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: 4 }}>
                  {form.formState.errors.fullName.message}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: "0.78rem", color: "#9A8F7E", marginBottom: 6, fontWeight: 600 }}>
              Mot de passe
            </label>
            <input
              {...form.register("password")}
              type="password"
              placeholder="••••••••"
              style={{
                width: "100%",
                padding: "12px 16px",
                background: "#0A0A08",
                border: "1px solid #2A2520",
                borderRadius: 10,
                color: "#F5F0E8",
                fontSize: "0.9rem",
                fontFamily: "'Sora', sans-serif",
                outline: "none",
              }}
            />
            {form.formState.errors.password && (
              <div style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: 4 }}>
                {form.formState.errors.password.message}
              </div>
            )}
          </div>

          {mode === "register" && (
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: "0.78rem", color: "#9A8F7E", marginBottom: 6, fontWeight: 600 }}>
                Confirmer le mot de passe
              </label>
              <input
                {...form.register("confirmPassword")}
                type="password"
                placeholder="••••••••"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  background: "#0A0A08",
                  border: "1px solid #2A2520",
                  borderRadius: 10,
                  color: "#F5F0E8",
                  fontSize: "0.9rem",
                  fontFamily: "'Sora', sans-serif",
                  outline: "none",
                }}
              />
              {form.formState.errors.confirmPassword && (
                <div style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: 4 }}>
                  {form.formState.errors.confirmPassword.message}
                </div>
              )}
            </div>
          )}

          {mode === "login" && <div style={{ marginBottom: 24 }} />}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              padding: 14,
              background: "linear-gradient(135deg, #F97316, #EAB308)",
              border: "none",
              borderRadius: 10,
              color: "#0A0A0A",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {submitting
              ? "Traitement..."
              : mode === "register"
                ? "Accepter l'invitation"
                : "Me connecter et accepter"}
          </button>
            </form>
          </>
        )}

        <a
          href="/"
          style={{
            display: "block",
            textAlign: "center",
            marginTop: 20,
            color: "#9A8F7E",
            fontSize: "0.85rem",
            textDecoration: "none",
          }}
        >
          ← Retour à l'accueil
        </a>
      </div>
    </div>
  );
};

export default AuthInvite;
